import { useCallback, useState } from 'react';

import { hayAutenticadorDelDispositivo } from '../passkeys/api/passkeyService';
import { debeOfrecerActivacion } from '../quick-login/rememberedUser';
import type { useRememberedUser } from '../quick-login/useRememberedUser';

type Args = {
  remembered: ReturnType<typeof useRememberedUser>;
  irAlDestino: () => void;
};

/**
 * La oferta de «Activar entrada rápida en este dispositivo» que se muestra tras
 * un ingreso completo con contraseña (y 2FA), antes de entrar a la app.
 */
export function useActivationOffer({ remembered, irAlDestino }: Args) {
  const [abierta, setAbierta] = useState(false);
  const { recuerdo, recordar, activar, descartar } = remembered;

  /**
   * Recuerda el usuario y decide si corresponde ofrecer. Devuelve true si abrió
   * la oferta (quien llama no debe entrar a la app todavía).
   */
  const decidir = useCallback(
    async (email: string): Promise<boolean> => {
      const recuerdoPrevio = recuerdo; // lo de ANTES de este ingreso
      recordar(email);
      const ofrecer = debeOfrecerActivacion({
        recuerdo: recuerdoPrevio,
        emailQueEntro: email,
        hayAutenticador: await hayAutenticadorDelDispositivo(),
      });
      if (ofrecer) setAbierta(true);
      return ofrecer;
    },
    [recuerdo, recordar],
  );

  const onActivada = useCallback(
    (email: string) => {
      activar(email);
      irAlDestino();
    },
    [activar, irAlDestino],
  );

  const onAhoraNo = useCallback(() => {
    descartar();
    irAlDestino();
  }, [descartar, irAlDestino]);

  return { abierta, decidir, onActivada, onAhoraNo };
}
